import { useFueFlags } from "@/features/feature-tour/use-fue-flags";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { useCallback, useMemo, useState } from "react";

/**
 * The FUE flag remembering that the user has been welcomed to the Sphere XG Viewer after the
 * migration from HoloBuilder.
 */
const HB_TO_SXG_MIGRATED_FLAG = "sxg:hb-to-sxg-migrated";

type UseWasWelcomedAfterHbToSxgMigration = {
  /** Whether the flags of the current user have been loaded. */
  isLoaded: boolean;

  /** Whether the user has already been welcomed after the migration from HoloBuilder. */
  wasWelcomed: boolean;

  /** Remembers the welcome for the account, or just this redirect when logged out. */
  markAsWelcomed(): void;
};

/**
 * Tracks whether the user has already been welcomed to the Sphere XG Viewer after the migration
 * from HoloBuilder, so that the welcome is only shown on the first redirect from the WebEditor.
 * Logged-out visitors are welcomed on every redirect instead.
 *
 * @returns Whether the user has already been welcomed, and a callback to remember that they have
 */
export function useWasWelcomedAfterHbToSxgMigration(): UseWasWelcomedAfterHbToSxgMigration {
  const { areFueFlagsLoaded, fueFlags, updateFueFlags } = useFueFlags();
  // Resolved before the project renders, unlike the user in the store, which is
  // only set after the project access check
  const { isLoggedIn } = useAuthContext();
  const [wasAnonymousWelcomed, setWasAnonymousWelcomed] = useState(false);

  const markAsWelcomed = useCallback(() => {
    if (!isLoggedIn) {
      setWasAnonymousWelcomed(true);
      return;
    }

    // The flag is not a feature tour topic, so it is kept in the untyped part of the flags
    updateFueFlags((current) => ({
      ...current,
      other: { ...current.other, [HB_TO_SXG_MIGRATED_FLAG]: true },
    }));
  }, [isLoggedIn, updateFueFlags]);

  return useMemo(
    () => ({
      isLoaded: !isLoggedIn || areFueFlagsLoaded,
      wasWelcomed: isLoggedIn
        ? fueFlags.other[HB_TO_SXG_MIGRATED_FLAG] === true
        : wasAnonymousWelcomed,
      markAsWelcomed,
    }),
    [
      isLoggedIn,
      areFueFlagsLoaded,
      fueFlags.other,
      wasAnonymousWelcomed,
      markAsWelcomed,
    ],
  );
}
